import styled, { css } from "styled-components";

import { HexColorInput } from "react-colorful";
import Grid from "@material-ui/core/Grid";

import colors, { transparentBg } from "core/colors";
import { regularFamily } from "core/fontStyles";
import { easingSeconds } from "core/animation";
import breakpoints from "core/breakpoints";

import Color from "core/ReusableIcons/Color";
import Palette from "core/ReusableIcons/Palette";

import { convertPxToRem } from "frontend/components/helpers";
import { isTransparent } from "./helpers";

// reusable constants and helpers:
const colorPickerButton = css`
    border-radius: 0;
    border: none;
    cursor: pointer;
`;

const swatchBorder = css`
    border: 1px solid ${colors.concrete};
`;

const TRANSPARENT = "transparent";

const getSwatchBGColor = color => {
    if (isTransparent(color)) {
        return TRANSPARENT;
    } else {
        return color ? color : colors.black;
    }
};

const setBgColor = ({ color }) => ({
    style: {
        backgroundColor: getSwatchBGColor(color)
    }
});

const swatchTransparentBg = css`
    ${({ color, mainSwatch }) =>
        isTransparent(color) &&
        css`
            opacity: 0.8;
            background-size: ${mainSwatch ? "10px" : "11px"};
            ${transparentBg}
        `}
`;

// styled components:
export const PopoverTrigger = styled.div.attrs(setBgColor)`
    ${colorPickerButton}
    ${swatchTransparentBg}
    width: ${({ size }) => size};
    height: ${({ size }) => size};
    &:hover {
        border: 2px solid ${colors.focus};
    }
    ${({ color }) =>
        color.toLowerCase() === colors.snow.toLowerCase() ||
        color.toLowerCase() === "#fff"
            ? swatchBorder
            : ""}
`;

export const PopoverContent = styled.div`
    .react-colorful {
        width: ${convertPxToRem({ px: 218 })};
        height: ${convertPxToRem({ px: 124 })};
        margin: auto;
        margin-top: ${convertPxToRem({ px: 14 })};
    }
    .react-colorful__hue-pointer,
    .react-colorful__alpha-pointer,
    .react-colorful__saturation-pointer {
        width: ${convertPxToRem({ px: 6 })};
        height: ${convertPxToRem({ px: 6 })};
        border-radius: ${convertPxToRem({ px: 3 })};
    }
    .react-colorful__pointer-fill {
        background-color: ${colors.snow} !important; /* forced to use important on the library styles - TODO: find a way to do this so important is not necessary*/
    }
    .react-colorful__saturation {
        width: ${convertPxToRem({ px: 218 })};
        border-radius: ${convertPxToRem({ px: 2 })};
    }
    .react-colorful__hue {
        margin-top: ${convertPxToRem({ px: 10 })};
        width: ${convertPxToRem({ px: 218 })};
        height: ${convertPxToRem({ px: 10 })};
        border-radius: ${convertPxToRem({ px: 10 })};
    }
`;

export const ColorSelectionContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const SelectedColor = styled.div.attrs(setBgColor)`
    display: inline-block;
    width: ${convertPxToRem({ px: 24 })};
    height: ${convertPxToRem({ px: 24 })};
    margin-right: ${convertPxToRem({ px: 8 })};
    border-radius: 50%;
    ${swatchTransparentBg}
    ${swatchBorder}
`;

export const ColorInputContainer = styled.div`
    flex: 1 1 auto;
    text-align: left;
`;

export const StyledHexColorInput = styled(HexColorInput)`
    width: ${convertPxToRem({ px: 79 })};
    height: ${convertPxToRem({ px: 28 })};
    padding: ${convertPxToRem({ px: 6 })} ${convertPxToRem({ px: 12 })};
    border-radius: ${convertPxToRem({ px: 3 })};
    border: 1px solid ${colors.concrete};
    color: ${colors.midnight};
    font: 300 ${convertPxToRem({ px: 14 })} ${regularFamily};
    line-height: ${convertPxToRem({ px: 16 })};
`;

export const PaletteButtonContainer = styled.div``;

export const PickerIconButton = styled.button`
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0;
    background: none;
    border-radius: 50%;
    width: ${convertPxToRem({ px: 16 })};
    height: ${convertPxToRem({ px: 16 })};

    &,
    svg,
    svg path {
        transition: all ${easingSeconds};
    }

    &:hover {
        ${Color} {
            filter: grayscale();
        }

        ${Palette} {
            path {
                fill: ${colors.midnight};
            }
        }
    }

    /* IE10 and IE11 support - filter: does not work in these browsers */
    @media ${breakpoints.browserSupport.ie10And11} {
        &:hover {
            box-shadow: 0 0 0 3px ${colors.storm};

            ${Color},
            ${Palette} {
                opacity: 0.6;
            }
        }
    }
`;

export const TitleContainer = styled.div`
    margin-bottom: ${convertPxToRem({ px: 10 })};
`;

// RecentColors

export const Swatch = styled.button.attrs(setBgColor)`
    ${colorPickerButton}
    width: ${convertPxToRem({ px: 22 })};
    height: ${convertPxToRem({ px: 22 })};

    ${({ color }) =>
        color.toLowerCase() === colors.snow.toLowerCase() ||
        color.toLowerCase() === "#fff"
            ? swatchBorder
            : ""}

    ${swatchTransparentBg}
`;

export const ContainerGrid = styled(Grid)`
    margin-top: ${convertPxToRem({ px: 12 })};
`;
